/**
 * Teslyn Field's side of the Edge Function archive-upload (docs/specs/cloud/file-archive.md §4), with the signed-in
 * user's Supabase JWT. Only Teslyn Cloud's address, the project's publishable key and the user's own token are
 * used: the Nextcloud's address and app password stay in Teslyn Cloud. Answers are turned into
 * {@link ArchiveSendError}s the engine acts on.
 */
import {
  ArchiveSendError,
  type ArchiveBegin,
  type ArchiveDone,
  type ArchiveFile,
  type ArchiveSender,
} from './archive.js';

export interface FunctionArchiveSenderOptions {
  /** The Supabase project's address, e.g. https://<ref>.supabase.co. */
  readonly supabaseUrl: string;
  readonly publishableKey: string;
  /** The signed-in user's access token, or null when signed out. */
  readonly token: () => Promise<string | null>;
  /** The error that tells the caller to sign in again (Field's SyncAuthError). */
  readonly signedOut: () => Error;
  readonly fetch?: typeof fetch;
}

interface Answer {
  readonly error?: string;
  readonly code?: string;
  readonly nextcloud?: string;
}

const OFFLINE =
  'Teslyn Cloud could not be reached. Files wait on this laptop and are archived when it is online.';

export class FunctionArchiveSender implements ArchiveSender {
  private readonly endpoint: string;
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly o: FunctionArchiveSenderOptions) {
    this.endpoint = `${o.supabaseUrl.replace(/\/+$/, '')}/functions/v1/archive-upload`;
    this.fetchImpl = o.fetch ?? ((...a) => fetch(...a));
  }

  private async headers(extra: Record<string, string>): Promise<Record<string, string>> {
    const jwt = await this.o.token();
    if (!jwt) throw this.o.signedOut();
    return { apikey: this.o.publishableKey, Authorization: `Bearer ${jwt}`, ...extra };
  }

  private async send<T>(url: string, init: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await this.fetchImpl(url, init);
    } catch {
      throw new ArchiveSendError('offline', OFFLINE);
    }
    const text = await res.text().catch(() => '');
    let body: Answer & Record<string, unknown> = {};
    try {
      body = text ? (JSON.parse(text) as typeof body) : {};
    } catch {
      body = {};
    }
    if (res.ok) return body as T;
    if (res.status === 401) throw this.o.signedOut();
    const message = body.error ?? `Teslyn Cloud did not archive the file (HTTP ${res.status}).`;
    switch (body.code) {
      case 'nextcloud':
        throw new ArchiveSendError('nextcloud', message, body.nextcloud ?? null);
      case 'not_set_up':
        throw new ArchiveSendError('nextcloud', message, 'not_set_up');
      case 'archive_off':
        throw new ArchiveSendError('off', message, body.code);
      case 'too_large':
      case 'empty':
        throw new ArchiveSendError('refused', message, body.code);
      case 'forbidden':
      case 'no_project':
      case 'not_uploaded':
      case 'changed':
        throw new ArchiveSendError('denied', message, body.code);
      default:
        throw new ArchiveSendError('server', message, body.code ?? null);
    }
  }

  private json<T>(fields: Record<string, unknown>): Promise<T> {
    return this.headers({ 'Content-Type': 'application/json' }).then((headers) =>
      this.send<T>(this.endpoint, { method: 'POST', headers, body: JSON.stringify(fields) }),
    );
  }

  private async binary<T>(fields: Record<string, string | number>, bytes: Uint8Array): Promise<T> {
    const q = new URLSearchParams(Object.entries(fields).map(([k, v]): [string, string] => [k, String(v)]));
    const headers = await this.headers({ 'Content-Type': 'application/octet-stream' });
    return this.send<T>(`${this.endpoint}?${q.toString()}`, {
      method: 'POST',
      headers,
      body: bytes as Uint8Array<ArrayBuffer>,
    });
  }

  begin(f: ArchiveFile): Promise<ArchiveBegin> {
    return this.json({ action: 'begin', ...f });
  }

  put(f: ArchiveFile, name: string, bytes: Uint8Array): Promise<ArchiveDone> {
    return this.binary({ action: 'put', ...f, name }, bytes);
  }

  async piece(f: ArchiveFile, name: string, n: number, bytes: Uint8Array): Promise<void> {
    await this.binary({ action: 'piece', ...f, name, n }, bytes);
  }

  finish(f: ArchiveFile, name: string): Promise<ArchiveDone> {
    return this.json({ action: 'finish', ...f, name });
  }

  /** The signed upload address of Teslyn Cloud storage carries its own token: the user's token is not sent there. */
  async putSigned(url: string, bytes: Uint8Array): Promise<void> {
    let res: Response;
    try {
      res = await this.fetchImpl(url, {
        method: 'PUT',
        // the publishable key is public (the API gateway may ask for it); the address's own token authorises
        headers: {
          apikey: this.o.publishableKey,
          'Content-Type': 'application/octet-stream',
          'x-upsert': 'true',
        },
        body: bytes as Uint8Array<ArrayBuffer>,
      });
    } catch {
      throw new ArchiveSendError('offline', OFFLINE);
    }
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      if (res.status === 413 || /too large|maximum allowed size/i.test(detail)) {
        throw new ArchiveSendError(
          'refused',
          'Teslyn Cloud storage refused the file as too large.',
          'too_large',
        );
      }
      throw new ArchiveSendError(
        'server',
        `Teslyn Cloud storage did not take the file (HTTP ${res.status}).`,
      );
    }
  }
}
